'use client';
import { useCompanyInfo } from '@/contexts/CompanyInfoContext';

interface DynamicCompanyInfoProps {
  type: 'logo' | 'companyName' | 'tagline' | 'email' | 'phone' | 'address' | 'social';
  variant?: 'light' | 'dark' | 'favicon';
  social?: 'facebook' | 'twitter' | 'linkedin' | 'instagram' | 'youtube' | 'github';
  className?: string;
  children?: (data: any) => React.ReactNode;
}

export default function DynamicCompanyInfo({ 
  type, 
  variant = 'light', 
  social, 
  className = '', 
  children 
}: DynamicCompanyInfoProps) {
  const { companyInfo, loading } = useCompanyInfo();

  if (loading || !companyInfo) {
    return <span className={`animate-pulse bg-gray-200 rounded inline-block ${className}`}>&nbsp;</span>;
  }

  const renderContent = () => {
    switch (type) {
      case 'logo':
        return (
          <img
            src={companyInfo.logo[variant as keyof typeof companyInfo.logo]}
            alt={companyInfo.companyName}
            className={className}
          />
        );
      
      case 'companyName':
        return <span className={className}>{companyInfo.companyName}</span>;
      
      case 'tagline':
        return <span className={className}>{companyInfo.tagline}</span>;
      
      case 'email':
        return (
          <a href={`mailto:${companyInfo.contact.email}`} className={className}>
            {companyInfo.contact.email}
          </a>
        );
      
      case 'phone':
        return (
          <a href={`tel:${companyInfo.contact.phone}`} className={className}>
            {companyInfo.contact.phone}
          </a>
        );
      
      case 'address':
        return <span className={className}>{companyInfo.address.fullAddress}</span>;
      
      case 'social':
        if (!social || !companyInfo.socialMedia[social]) return null;
        return (
          <a
            href={companyInfo.socialMedia[social]}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
          >
            {children ? children(companyInfo.socialMedia[social]) : companyInfo.socialMedia[social]}
          </a>
        );
      
      default:
        return null;
    }
  };

  if (children && type !== 'social') {
    return <>{children(companyInfo)}</>;
  }

  return <>{renderContent()}</>;
}